import type { RankingEntry } from '../api/ranking';
import { CONFIG } from '../config/gameConfig';
import { intersects } from '../core/CollisionDetector';
import { InputManager } from '../core/InputManager';
import type { GameState, InputAction } from '../types';
import { Bloomy } from './entities/Bloomy';
import { Cloud } from './entities/Cloud';
import { Grave } from './entities/Grave';
import { Ground } from './entities/Ground';
import { Obstacle, type ObstacleKind } from './entities/Obstacle';

interface GraveQueueItem {
  score: number;
  companyName: string;
  nickname: string;
}

export interface GameCallbacks {
  onScoreChange?: (score: number, highScore: number) => void;
  onStateChange?: (state: GameState) => void;
  onGameOver?: (score: number, highScore: number) => void;
}

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  // 논리 높이는 624 고정, 가로는 컨테이너에 맞춰 동적(여백 없이 채우기).
  private logicalWidth: number = CONFIG.canvas.width;
  private readonly input: InputManager;
  private readonly player = new Bloomy();
  private readonly ground = new Ground();
  private readonly obstacles: Obstacle[] = [];
  private readonly clouds: Cloud[] = [];
  private readonly graves: Grave[] = [];
  private graveQueue: GraveQueueItem[] = [];
  private gravePointer = 0;

  private state: GameState = 'waiting';
  private speed: number = CONFIG.game.initialSpeed;
  private rawScore = 0;
  private highScore = 0;
  private framesUntilObstacle = 60;
  private framesUntilCloud = 30;
  private rafId: number | null = null;
  // 화면 refresh rate(60/90/120/144Hz)와 무관하게 60FPS 시뮬레이션으로 정규화.
  // accumulator에 경과 시간을 쌓고 FIXED_STEP_MS 단위로 update를 끊어 호출.
  private static readonly FIXED_STEP_MS = 1000 / 60;
  private static readonly MAX_FRAME_MS = 250;
  private lastTime = 0;
  private accumulator = 0;
  // false 면 'start' / 'restart' 입력을 무시한다 (오늘 플레이 횟수 소진 시).
  private startAllowed = true;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly callbacks: GameCallbacks = {},
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.canvas = canvas;
    this.ctx = ctx;
    this.input = new InputManager();
    this.input.on(this.handleAction);
    this.highScore = Number(localStorage.getItem(CONFIG.score.storageKey) ?? 0);
    this.resize();
    // 컨테이너(FE iframe) 크기 변화에 맞춰 캔버스 해상도·가로 플레이영역 갱신.
    new ResizeObserver(() => this.resize()).observe(canvas);
  }

  // 캔버스를 컨테이너에 꽉 채운다. 세로 624 논리값을 균일 스케일로 맞추고 가로는 동적.
  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const displayW = Math.max(1, rect.width);
    const displayH = Math.max(1, rect.height);
    this.canvas.width = Math.round(displayW * dpr);
    this.canvas.height = Math.round(displayH * dpr);
    const scale = (displayH / CONFIG.canvas.height) * dpr;
    this.ctx.setTransform(scale, 0, 0, scale, 0, 0);
    this.logicalWidth = this.canvas.width / scale;
  }

  start(): void {
    if (this.rafId === null) {
      this.lastTime = 0;
      this.accumulator = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }
    this.notifyState();
    this.notifyScore();
  }

  destroy(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.input.destroy();
  }

  getState(): GameState {
    return this.state;
  }

  setStartAllowed(allowed: boolean): void {
    this.startAllowed = allowed;
  }

  setUpcomingGraves(entries: ReadonlyArray<RankingEntry>): void {
    this.graveQueue = entries
      .map<GraveQueueItem>((entry) => ({
        score: entry.score,
        companyName: entry.companyName,
        nickname: entry.nickname,
      }))
      .sort((a, b) => a.score - b.score);
    this.gravePointer = this.findGravePointerForScore(this.rawScore);
  }

  private readonly handleAction = (action: InputAction): void => {
    switch (action) {
      case 'jump':
        // jump 는 running 중 점프 전용. waiting 에서 시작 X (Space 는 별도 'start' 액션으로 emit).
        // 'over' 상태에서는 jump 로 재시작하지 않음 — 닉네임 입력 보호.
        if (this.state === 'running') this.player.jump();
        return;
      case 'start':
        if (this.state === 'waiting' && this.startAllowed) this.beginRun();
        return;
      case 'duck-start':
        if (this.state === 'running') this.player.startDuck();
        return;
      case 'duck-end':
        this.player.endDuck();
        return;
      case 'restart':
        if ((this.state === 'over' || this.state === 'waiting') && this.startAllowed) {
          this.beginRun();
        }
        return;
    }
  };

  private beginRun(): void {
    this.player.reset();
    this.obstacles.length = 0;
    this.clouds.length = 0;
    this.graves.length = 0;
    this.gravePointer = 0;
    this.ground.reset();
    this.speed = CONFIG.game.initialSpeed;
    this.rawScore = 0;
    this.framesUntilObstacle = 60;
    this.framesUntilCloud = 30;
    this.transition('running');
    this.notifyScore();
  }

  private transition(state: GameState): void {
    this.state = state;
    this.notifyState();
  }

  private readonly tick = (now: number): void => {
    if (this.lastTime === 0) this.lastTime = now;
    const elapsed = Math.min(now - this.lastTime, Game.MAX_FRAME_MS);
    this.lastTime = now;
    this.accumulator += elapsed;
    while (this.accumulator >= Game.FIXED_STEP_MS) {
      this.update();
      this.accumulator -= Game.FIXED_STEP_MS;
    }
    this.render();
    this.rafId = requestAnimationFrame(this.tick);
  };

  private update(): void {
    if (this.state !== 'running') return;

    this.speed = Math.min(CONFIG.game.maxSpeed, this.speed + CONFIG.game.speedAcceleration);
    this.player.update();
    this.ground.update(this.speed);

    this.maybeSpawnObstacle();
    this.maybeSpawnCloud();
    this.maybeSpawnGraves();

    for (const obstacle of this.obstacles) obstacle.update(this.speed);
    for (const cloud of this.clouds) cloud.update(this.speed);
    for (const grave of this.graves) grave.update(this.speed);

    while (this.obstacles[0]?.isOffscreen()) this.obstacles.shift();
    while (this.clouds[0]?.isOffscreen()) this.clouds.shift();
    while (this.graves[0]?.isOffscreen()) this.graves.shift();

    if (this.detectCollision()) {
      this.gameOver();
      return;
    }

    this.rawScore += CONFIG.score.pointsPerFrame;
    this.notifyScore();
  }

  private maybeSpawnObstacle(): void {
    this.framesUntilObstacle -= 1;
    if (this.framesUntilObstacle > 0) return;
    const kinds: ObstacleKind[] =
      this.speed > 8
        ? ['cactus-small', 'cactus-large', 'pterodactyl']
        : ['cactus-small', 'cactus-large'];
    const kind = kinds[Math.floor(Math.random() * kinds.length)];
    if (!kind) return;
    this.obstacles.push(new Obstacle(kind, this.logicalWidth));
    this.framesUntilObstacle =
      CONFIG.obstacle.minGapFrames + Math.floor(Math.random() * CONFIG.obstacle.extraGapFrames);
  }

  private maybeSpawnCloud(): void {
    this.framesUntilCloud -= 1;
    if (this.framesUntilCloud > 0) return;
    this.clouds.push(new Cloud(this.logicalWidth));
    this.framesUntilCloud =
      CONFIG.cloud.minGapFrames + Math.floor(Math.random() * CONFIG.cloud.extraGapFrames);
  }

  // 다가오는 grave 의 정확한 픽셀 거리를 계산해 spawn:
  //   distance(px) = (target.score - currentScore) / pointsPerFrame * speed
  // 거리값이 화면 안쪽이 될 때까지 모아서 한 번에 push.
  private maybeSpawnGraves(): void {
    const playerCenterX = CONFIG.player.startX + CONFIG.player.width / 2;
    const visibleRange = this.logicalWidth - playerCenterX;
    while (this.gravePointer < this.graveQueue.length) {
      const next = this.graveQueue[this.gravePointer];
      if (!next) break;
      const distance =
        ((next.score - this.rawScore) / CONFIG.score.pointsPerFrame) * this.speed;
      if (distance > visibleRange) break;
      const spawnX = Math.max(playerCenterX, playerCenterX + distance);
      this.graves.push(new Grave(next.score, next.companyName, next.nickname, spawnX));
      this.gravePointer += 1;
    }
  }

  private findGravePointerForScore(score: number): number {
    let i = 0;
    while (i < this.graveQueue.length) {
      const item = this.graveQueue[i];
      if (!item || item.score >= score) break;
      i += 1;
    }
    return i;
  }

  private detectCollision(): boolean {
    const playerBounds = this.player.bounds();
    for (const obstacle of this.obstacles) {
      if (intersects(playerBounds, obstacle.bounds())) return true;
    }
    return false;
  }

  private gameOver(): void {
    this.player.alive = false;
    const final = Math.floor(this.rawScore);
    if (final > this.highScore) {
      this.highScore = final;
      localStorage.setItem(CONFIG.score.storageKey, String(this.highScore));
    }
    this.transition('over');
    this.callbacks.onGameOver?.(final, this.highScore);
  }

  private render(): void {
    const { ctx } = this;
    ctx.clearRect(0, 0, this.logicalWidth, CONFIG.canvas.height);
    for (const cloud of this.clouds) cloud.draw(ctx);
    this.ground.draw(ctx, this.logicalWidth);
    for (const grave of this.graves) grave.draw(ctx);
    for (const obstacle of this.obstacles) obstacle.draw(ctx);
    this.player.draw(ctx);
  }

  private notifyState(): void {
    this.callbacks.onStateChange?.(this.state);
  }

  private notifyScore(): void {
    this.callbacks.onScoreChange?.(Math.floor(this.rawScore), this.highScore);
  }
}
